/**
 * Plain text out of a rich-text HTML field, for the places that show a
 * teaser rather than the document.
 *
 * The full field is rendered as markup by whoever owns the page (`FaqEntry`,
 * `ProjectSections`, the services and CMS pages, all via
 * `dangerouslySetInnerHTML` over server-sanitised HTML). A preview cannot do
 * that: it is a clamped line or two inside someone else's layout, and
 * injecting a fragment there drags in `<h2>`/`<ul>`/`<img>` and their block
 * spacing. Printing the field raw is worse still — React escapes plain
 * children rather than parsing them, so the visitor reads the tags.
 *
 * So the tags come out and the text stays. This is a *presentation* helper,
 * not a sanitiser: it must only ever be given HTML the API has already
 * sanitised on write, and its result must be rendered as text (never fed
 * back into `dangerouslySetInnerHTML`).
 */
export function htmlToPlainText(html: string): string {
  return (
    html
      // Block-level boundaries become spaces, so "</p><p>" does not weld the
      // last word of one paragraph to the first of the next.
      .replace(/<\/(?:p|div|li|h[1-6]|tr|blockquote)>/gi, ' ')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<[^>]+>/g, '')
      // The five entities the sanitiser can emit for characters that would
      // otherwise be markup. `&amp;` is unescaped last: doing it first would
      // turn "&amp;lt;" — a literal "&lt;" the author typed — into "<".
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#(?:39|x27);/gi, "'")
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      .trim()
  );
}
